import ValueCollection from './ValueCollection.js';
import CharacteristicCollection from './geodata/CharacteristicCollection.js';
/**
 * @typedef DataRetrieverResult
 * @memberof DataRetriever
 * @property {ValueCollection} values
 * @property {geodata.FeatureCollection} featureCollection
 */
/**
 * @classdesc
 * Class responsible for retrieving and formatting data for features.
 */
class DataRetriever {
    /**
     * @param {feature-references.FeatureReferenceCollection} featureReferenceCollection
     * @param {data-references.DataImplementation[]} implementations
     */
    constructor(featureReferenceCollection, implementations) {
        this.featureReferenceCollection = featureReferenceCollection;
        this.implementations = implementations;
    }
    /**
     * Retrieve values for the specified data implementations & feature
     * collection. Returning results for all references as well as metadata.
     *
     * @return {Promise<DataRetrieverResult>}
     */
    retrieve() {
        const { featureReferenceCollection, implementations } = this;
        const dataReferenceCollections = dataReferenceCollectionsFrom(implementations);
        const normalizedPairs = dataReferenceCollectionPairsFrom(implementations);
        const features = featureReferenceCollection.loadFeatureCollection();
        const data = Promise.all(dataReferenceCollections
            .map(collection => collection.ready())).then(uniqueDataReferencesFrom);
        return Promise.all([features, data])
            .then(([featureCollection, dataReferences]) => {
            return (CharacteristicCollection
                .fromReferences(featureCollection, dataReferences)
                .then((characteristicCollection) => ({
                featureCollection,
                characteristicCollection,
            })));
        })
            .then(({ featureCollection, characteristicCollection }) => {
            const values = ValueCollection.from(featureCollection, characteristicCollection, { normalize: normalizedPairs });
            return {
                featureCollection,
                values,
            };
        });
    }
    /**
     * @param {feature-references.FeatureReferenceCollection} featureReferenceCollection
     * @param {data-references.DataImplementation[]} implementations
     * @return {Promise<DataRetriever>}
     */
    static for(featureReferenceCollection, implementations) {
        return Promise.resolve(new DataRetriever(featureReferenceCollection, implementations));
    }
}
DataRetriever.ValueCollection = ValueCollection;
/**
 * @private
 * @param {DataImplementation[]} dataImplementations
 * @return {DataReferenceCollection[]}
 */
function dataReferenceCollectionsFrom(dataImplementations) {
    return (dataImplementations
        .map(implementation => implementation.listAllDataReferences())
        .flat());
}
/**
 * @private
 * @param {DataReferenceCollection[]} dataReferenceCollections
 * @return {DataReference[]}
 */
function uniqueDataReferencesFrom(dataReferenceCollections) {
    return Object.values(dataReferenceCollections
        .map(collection => collection.variables)
        .flat()
        .reduce((memo, dataReference) => {
        memo[dataReference.key] = dataReference;
        return memo;
    }, {}));
}
/**
 * @private
 * @param {DataImplementation[]} dataImplementations
 * @return {DataReferenceCollection[][]}
 */
function dataReferenceCollectionPairsFrom(dataImplementations) {
    return dataImplementations
        .filter(implementation => implementation.hasNormalization)
        .map(implementation => implementation.listAllDataReferences());
}
export default DataRetriever;
